// Local-first persistence: reading progress, bookmarks, highlights, streaks.
import { idbSet } from "./idb";

const K = {
  progress: "fw_progress",
  bookmarks: "fw_bookmarks",
  highlights: "fw_highlights",
  last: "fw_last",
  streak: "fw_streak",
  read: "fw_read_chapters",
  memory: "fw_memory",
};

const read = (k, def) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : def;
  } catch {
    return def;
  }
};
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

export const getLast = () => read(K.last, null);
export const setLast = (book, chapter, bookName) => {
  const entry = { book, chapter, bookName, at: Date.now() };
  write(K.last, entry);
  // mirror to IndexedDB so the service worker can run 48h reminder checks
  idbSet("last", entry).catch(() => {});
};

// chapter completion set, keyed "BID_C"
export const getReadChapters = () => read(K.read, {});
export const markChapterRead = (book, chapter) => {
  const r = getReadChapters();
  r[`${book}_${chapter}`] = Date.now();
  write(K.read, r);
  bumpStreak();
};
export const isChapterRead = (book, chapter) =>
  !!getReadChapters()[`${book}_${chapter}`];
export const readCount = () => Object.keys(getReadChapters()).length;

// manual progress editing (e.g. logging a paper-Bible reading)
export const setChaptersRead = (pairs, value) => {
  const r = getReadChapters();
  let changed = false;
  pairs.forEach(([book, chapter]) => {
    const key = `${book}_${chapter}`;
    if (value) {
      if (!r[key]) { r[key] = Date.now(); changed = true; }
    } else if (r[key]) {
      delete r[key]; changed = true;
    }
  });
  if (changed) {
    write(K.read, r);
    if (value) bumpStreak();
  }
  return r;
};

// bookmarks: array of {book, bookName, chapter, verse, text}
export const getBookmarks = () => read(K.bookmarks, []);
export const toggleBookmark = (item) => {
  const list = getBookmarks();
  const idx = list.findIndex(
    (b) => b.book === item.book && b.chapter === item.chapter && b.verse === item.verse
  );
  if (idx >= 0) list.splice(idx, 1);
  else list.unshift(item);
  write(K.bookmarks, list);
  return idx < 0;
};
export const isBookmarked = (book, chapter, verse) =>
  getBookmarks().some((b) => b.book === book && b.chapter === chapter && b.verse === verse);

// highlights: map "BID_C_V" -> color
export const getHighlights = () => read(K.highlights, {});
export const setHighlight = (book, chapter, verse, color) => {
  const h = getHighlights();
  const key = `${book}_${chapter}_${verse}`;
  if (!color) delete h[key];
  else h[key] = color;
  write(K.highlights, h);
};
export const getHighlight = (book, chapter, verse) =>
  getHighlights()[`${book}_${chapter}_${verse}`] || null;

// streak (days)
const todayStr = () => new Date().toISOString().slice(0, 10);
export const getStreak = () => read(K.streak, { count: 0, last: null });
function bumpStreak() {
  const s = getStreak();
  const today = todayStr();
  if (s.last === today) return;
  const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  s.count = s.last === y ? s.count + 1 : 1;
  s.last = today;
  write(K.streak, s);
  // mirror to IndexedDB so the service worker can run streak-risk reminders
  idbSet("streak", s).catch(() => {});
}

// Reading Memory Score: per-chapter best quiz result + lifetime aggregate
// store: { "BID_C": { correct, total, at }, ... }
export const getMemoryScores = () => read(K.memory, {});
export const recordMemoryScore = (book, chapter, correct, total) => {
  const m = getMemoryScores();
  const key = `${book}_${chapter}`;
  const prev = m[key];
  // keep the best attempt per chapter
  if (!prev || correct > prev.correct) {
    m[key] = { correct, total, at: Date.now() };
    write(K.memory, m);
  }
  return m[key];
};
export const getMemoryStats = () => {
  const m = getMemoryScores();
  const entries = Object.values(m);
  const chapters = entries.length;
  const correct = entries.reduce((a, e) => a + e.correct, 0);
  const total = entries.reduce((a, e) => a + e.total, 0);
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  return { chapters, correct, total, pct };
};
